import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Createable from "containers/Select";
import { useSelector } from "react-redux";
import { userSelector } from "store/features/authSlice";
import {
  isUnregisteredGstTreatment,
  normalizeCurrencyInput,
  parseStrictNumber,
} from "utils/common";

const GST = ({
  formdata,
  setFormdata,
  errorMessage,
  setErrorMessage,
  mode,
  gstTreatment,
  showIgstTotal = false,
}) => {
  const { gstTreatmentsList } = useSelector(userSelector);
  const [gstTreatmentListOptions, setTreatmentsList] = useState([]);

  const gstTypeOptions = [
    { label: "CGST & SGST", value: "CGST & SGST" },
    { label: "IGST", value: "IGST" },
  ];
  const isUnregistered = isUnregisteredGstTreatment(formdata?.gstTreatment);
  const requiredMarker = isUnregistered ? "" : "*";
  const splitIgstTotal =
    parseStrictNumber(formdata?.sgst) + parseStrictNumber(formdata?.cgst);

  useEffect(() => {
    if (Array.isArray(gstTreatmentsList)) {
      const updated = gstTreatmentsList.map((item) => ({
        label: `${item.name} - ${item.description}`,
        value: `${item.name} - ${item.description}`,
      }));
      setTreatmentsList(updated);
    } else {
      setTreatmentsList([]);
    }
  }, [gstTreatmentsList]);

  return (
    <SuiBox>
      <Grid container spacing={3}>
        {gstTreatment && (
          <Grid item xs={12} lg={4}>
            <SuiBox mr={2}>
              <SuiTypography mb={1} variant="h6">
                GST Treatment
              </SuiTypography>

              <Createable
                disabled={mode === "view"}
                options={gstTreatmentListOptions ?? []}
                error={errorMessage?.gstTreatment}
                value={formdata?.gstTreatment}
                style={{ borderRadius: "0.5rem", fontSize: 14 }}
                inputStyle={{
                  width: 190,
                  height: "1.800rem",
                  borderRadius: "0.5rem",
                }}
                placeholder="Choose gst treatment"
                onChange={(e) => {
                  setErrorMessage({ ...errorMessage, gstTreatment: false });
                  setFormdata({
                    ...formdata,
                    gstTreatment: e,
                  });
                }}
              />
            </SuiBox>
          </Grid>
        )}

        <Grid item xs={12} lg={4}>
          <SuiTypography mb={1} variant="h6">
            GST Number{requiredMarker}
          </SuiTypography>
          <SuiInput
            value={formdata?.gstNumber}
            disabled={mode === "view"}
            type="text"
            name="gstNumber"
            onChange={(e) => {
              setFormdata({ ...formdata, gstNumber: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <SuiTypography mb={1} variant="h6">
            GST Type{requiredMarker}
          </SuiTypography>
          <Createable
            disabled={mode === "view"}
            options={gstTypeOptions ?? []}
            error={errorMessage?.gstType}
            value={formdata?.gstType}
            style={{ borderRadius: "0.5rem", fontSize: 14 }}
            inputStyle={{
              width: 190,
              height: "1.800rem",
              borderRadius: "0.5rem",
            }}
            placeholder="Choose gst type"
            onChange={(e) => {
              const currentSgst = parseStrictNumber(formdata?.sgst);
              const currentCgst = parseStrictNumber(formdata?.cgst);
              const currentIgst = parseStrictNumber(formdata?.igst);
              const currentGst = parseStrictNumber(formdata?.gst);
              const splitGst = currentSgst + currentCgst;
              const availableGst = splitGst || currentIgst || currentGst;
              const nextIsIgst = e?.value === "IGST";

              setErrorMessage({ ...errorMessage, gstType: false });
              setFormdata({
                ...formdata,
                gstType: e,
                igst: nextIsIgst ? currentIgst || availableGst : currentIgst,
                sgst: nextIsIgst
                  ? currentSgst
                  : currentSgst || availableGst / 2,
                cgst: nextIsIgst
                  ? currentCgst
                  : currentCgst || availableGst / 2,
              });
            }}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          {formdata?.gstType?.value !== "IGST" ? (
            <Grid container spacing={1}>
              <Grid item xs={showIgstTotal ? 3 : 6}>
                <SuiTypography mb={1} variant="h6">
                  SGST%{requiredMarker}
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  value={formdata?.sgst}
                  type="number"
                  name="sgst"
                  onChange={(e) => {
                    const sgst = normalizeCurrencyInput(e.target.value);
                    if (sgst === null) return;
                    setFormdata({
                      ...formdata,
                      sgst,
                      igst:
                        parseStrictNumber(sgst) +
                        parseStrictNumber(formdata?.cgst),
                    });
                  }}
                />
              </Grid>
              <Grid item xs={showIgstTotal ? 3 : 6}>
                <SuiTypography mb={1} variant="h6">
                  CGST%{requiredMarker}
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  value={formdata?.cgst}
                  type="number"
                  name="cgst"
                  onChange={(e) => {
                    const cgst = normalizeCurrencyInput(e.target.value);
                    if (cgst === null) return;
                    setFormdata({
                      ...formdata,
                      cgst,
                      igst:
                        parseStrictNumber(formdata?.sgst) +
                        parseStrictNumber(cgst),
                    });
                  }}
                />
              </Grid>
              {showIgstTotal && (
                <Grid item xs={1}>
                  <SuiBox
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    pt={4}
                  >
                    <SuiTypography variant="button" fontWeight="bold">
                      OR
                    </SuiTypography>
                  </SuiBox>
                </Grid>
              )}
              {showIgstTotal && (
                <Grid item xs={5}>
                  <SuiTypography mb={1} variant="h6">
                    IGST%
                  </SuiTypography>
                  <SuiInput
                    disabled={mode === "view"}
                    value={formdata?.igst || splitIgstTotal}
                    type="number"
                    name="igst"
                    onChange={(e) => {
                      const igst = normalizeCurrencyInput(e.target.value);
                      if (igst === null) return;

                      const splitTax = parseStrictNumber(igst) / 2;
                      setFormdata({
                        ...formdata,
                        igst,
                        sgst: splitTax,
                        cgst: splitTax,
                      });
                    }}
                  />
                </Grid>
              )}
            </Grid>
          ) : (
            <SuiBox>
              <SuiTypography mb={1} variant="h6">
                IGST%{requiredMarker}
              </SuiTypography>
              <SuiInput
                disabled={mode === "view"}
                value={formdata?.igst}
                type="number"
                name="igst"
                onChange={(e) => {
                  const igst = normalizeCurrencyInput(e.target.value);
                  if (igst === null) return;
                  setFormdata({ ...formdata, igst });
                }}
              />
            </SuiBox>
          )}
        </Grid>
        <Grid item xs={4}>
          <SuiBox>
            <SuiTypography mb={1} variant="h6">
              GST Comments
            </SuiTypography>
            <SuiInput
              disabled={mode === "view"}
              value={formdata?.gstComment}
              type="text"
              name="gstComments"
              onChange={(e) => {
                setFormdata({ ...formdata, gstComment: e.target.value });
              }}
            />
          </SuiBox>
        </Grid>
      </Grid>
    </SuiBox>
  );
};

export default GST;
